import './pixlee-feed.scss';
import React, { Suspense, useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import PixleeAnalytics from '../../../utils/pixlee-analytics';
import { PixleeAlbum } from './pixlee-types';
import ReactDOM from 'react-dom';
import { useMediaQuery } from '@material-ui/core';
import { MUI_MOBILE_BREAKPOINT } from '../../../utils/constants';
import { useFetch } from 'usehooks-ts';
import { ApolloProvider } from '@apollo/client';
import defaultClient from '../apolloClient';
import { notNil } from '../../../utils/typescriptUtils';
import { PixleeFeedContainer } from './PixleeFeedContainer';

import SwiperCore, { A11y, Navigation } from 'swiper';
import 'swiper/swiper.scss';
import 'swiper/components/navigation/navigation.scss';
import ErrorBoundary from '../../../utils/react/ErrorBoundary';
import { isLocal, SiteSettings } from '../../../utils/environment';
SwiperCore.use([A11y, Navigation]);

const tmp = new Date();
tmp.setUTCMinutes(0);
tmp.setUTCSeconds(0);
const cacheBustParam = Math.floor(Number(tmp) + 1000); // Bust cache every second so pixlee query results with a fresh data and prices

function PixleeFeed({
  isPDP = false,
  isPageLayout = false,
  requestURL,
}: {
  isPDP?: boolean;
  isPageLayout?: boolean;
  requestURL: string;
}) {
  const { data, error } = useFetch<PixleeData>(`${requestURL}&cacheTime=${cacheBustParam}`);
  const [uniqueHandles, setUniqueHandles] = useState<string[]>([]);
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  // "Page layout" mode support
  const [viewGrid, setViewGrid] = useState(false);
  const [postId, setPostId] = useState<number | null>(null);
  useEffect(() => {
    if (postId && !viewGrid) {
      const post = document.getElementById(postId.toString());
      if (post) {
        const yOffset = -107; // Header + view switch panel
        setTimeout(() => {
          // Timeout to make sure the view is changed and we are getting the right coords
          const y = post?.getBoundingClientRect()?.top + window.scrollY + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
          setPostId(null);
        }, 500);
      }
    }
  }, [viewGrid, postId]);

  const viewGridChildren = [];
  // eslint-disable-next-line no-plusplus
  for (let index = 0; index < 9; index++) {
    viewGridChildren.push(<div className="view__grid--child" key={index} />);
  }
  const viewThePost = (id: number) => {
    setViewGrid(false);
    setPostId(id);
  };

  const switchView = () => {
    setViewGrid(!viewGrid);
  };
  // "Page layout" mode support

  if (data) {
    PixleeAnalytics.initPixleeAnalytics({ albumId: data.album_id.toString() });
    if (uniqueHandles.length === 0) {
      const handles = getHandles(data.data);
      if (handles && handles.length > 0) setUniqueHandles(handles);
    }
  }

  if (error) console.log('Error fetching the Pixlee API. Please see Network Tab for more details.', error);

  // return null
  if ((data && uniqueHandles.length === 0) || error) return null;

  // Loading state
  if (!data || uniqueHandles.length === 0)
    return (
      <div className="loading-spinners mt-5 mb-5">
        <div className="d-flex">{spinners.map((spinner) => spinner)}</div>
        <p className="large-p">Loading the feed</p>
      </div>
    );
  const isBeauty = isPDP && window.server_pdp_data?.product.type.includes('Beauty');
  //Copy overrides from Theme Settings
  const hpPixleeTitle = window.page_data?.hp_title || 'ALO IN THE WILD';
  const hpPixleeSubtitle = window.page_data?.hp_subtitle || `${isMobile ? 'Tap' : 'Click'} to shop the looks you love!`;
  const {
    pdpPixleeTitle,
    pdpPixleeSubtitle,
    pdpBeautyPixleeTitle,
    pdpBeautyPixleeSubtitle,
  } = SiteSettings?.pdpPixleeCopy;

  const title =
    isPDP && isBeauty
      ? pdpBeautyPixleeTitle || 'Looking Good, ALO Fam!'
      : isPDP
      ? pdpPixleeTitle || 'Style inspiration'
      : hpPixleeTitle;

  const subtitle =
    isPDP && isBeauty
      ? pdpBeautyPixleeSubtitle || 'Check out these major glow ups.'
      : isPDP
      ? pdpPixleeSubtitle || 'Shop our best looks, styled by you'
      : hpPixleeSubtitle;

  return (
    <div id="PixleeFeed" className={`pixlee-feed ${isPageLayout ? 'page-layout' : ''}`}>
      {isPageLayout && isMobile ? (
        <div className="container view-panel-container">
          <div className="row">
            <div className="col-12 view__panel">
              <p className="pre-headline">VIEW:</p>
              <div
                className={viewGrid ? 'view__full-width' : 'view__full-width active'}
                role="button"
                onClick={switchView}
              />
              <div className={viewGrid ? 'view__grid active' : 'view__grid'} role="button" onClick={switchView}>
                {viewGridChildren.map((child) => child)}
              </div>
            </div>
          </div>
        </div>
      ) : !isPageLayout ? (
        <div className="container-fluid">
          <div className="row headings__row">
            <div className="col-12">
              <h3 className="h3 semibold black uppercase center">{title}</h3>
              <p className="body-large black center">{subtitle}</p>
            </div>
          </div>
        </div>
      ) : null}
      <PixleeFeedContainer
        pixleeData={data.data}
        handles={uniqueHandles}
        albumId={data.album_id}
        isPDP={isPDP}
        title={title}
        viewGrid={viewGrid}
        isPageLayout={isPageLayout}
        viewThePost={viewThePost}
        isMobile={isMobile}
      />
    </div>
  );
}

export default PixleeFeed;

// ? [REVISIT] Move to somewhere more appropriate ?
export const renderPixleeFeedHP = (mountNode: HTMLElement) => {
  const DefaultPostsPerRequest = '24';
  const requestURL =
    window.page_data?.request_url ||
    `https://distillery.pixlee.com/api/v2/albums/14140960/photos?sort=%7B%22recency%22:true,%22desc%22:true%7D&page=1&per_page=${DefaultPostsPerRequest}&api_key=TB6jIdhJVSuV0LWK7elH&today=tomorrow`;
  ReactDOM.render(
    <ErrorBoundary className="centered-message" key={(!isLocal || new Date()).toString()}>
      <Suspense fallback={null}>
        <ApolloProvider client={defaultClient}>
          <PixleeFeed requestURL={requestURL} />
        </ApolloProvider>
      </Suspense>
    </ErrorBoundary>,
    mountNode
  );
};

const getHandles = (pixleeData: PixleeAlbum[] | undefined) => {
  if (!pixleeData) return [];

  const productsFromFeed = pixleeData.flatMap((post) => post.products);
  const uniqueProductsFromFeed = Array.from(new Set(productsFromFeed.map((product) => product.id)))
    .map((uniqueId) => {
      return productsFromFeed.find((product) => product.id === uniqueId);
    })
    .filter(notNil);
  // Pixlee has mapped product handles to "sku"
  const uniqueProductHandles = uniqueProductsFromFeed?.map((item) => item?.sku.replace('-simple', ''));
  return uniqueProductHandles;
};

const spinners: React.ReactNodeArray = [];
// eslint-disable-next-line no-plusplus
for (let index = 0; index < 4; index++) {
  spinners.push(<Spinner style={{ width: '1rem', height: '1rem' }} type="grow" color="dark" key={index} />);
}

interface PixleeData {
  album_id: number;
  data: PixleeAlbum[];
}
